import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { posApi, productsApi, storesApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ReceiptModal from '../components/pos/ReceiptModal';
import BarcodeScannerModal from '../components/pos/BarcodeScannerModal';

const fmt = (n) => Number(n || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CashierPage() {
    const { user } = useAuth();

    // Products
    const [allProducts, setAllProducts] = useState([]);
    const [query, setQuery] = useState('');
    const scanInputRef = useRef(null);

    // Cart
    const [cart, setCart] = useState([]);
    const [taxRate, setTaxRate] = useState(0);

    // Customer
    const [customerQuery, setCustomerQuery] = useState('');
    const [customerSuggestions, setCustomerSuggestions] = useState([]);
    const [customer, setCustomer] = useState(null);
    const [showNewCustomer, setShowNewCustomer] = useState(false);
    const [newCust, setNewCust] = useState({ first_name: '', last_name: '', phone: '', email: '' });
    const [custLoading, setCustLoading] = useState(false);

    // Loyalty
    const [loyaltyConfig, setLoyaltyConfig] = useState(null);
    const [redeemPoints, setRedeemPoints] = useState(false);

    // Discount
    const [discountInput, setDiscountInput] = useState('');

    // Payment
    const [payMethod, setPayMethod] = useState('cash'); // cash | transfer | pos | split
    const [cashTendered, setCashTendered] = useState('');
    const [payReference, setPayReference] = useState('');
    const [splitCash, setSplitCash] = useState('');
    const [splitTransfer, setSplitTransfer] = useState('');
    const [splitReference, setSplitReference] = useState('');

    // UI
    const [processing, setProcessing] = useState(false);
    const [receipt, setReceipt] = useState(null);
    const [scannerOpen, setScannerOpen] = useState(false);
    const [scanMessage, setScanMessage] = useState('');
    const hardwareBufferRef = useRef('');
    const hardwareTimerRef = useRef(null);

    // ── Load ──────────────────────────────────────────────────────────────────
    useEffect(() => {
        productsApi.list({ limit: 500, status: 'active' }).then((res) => {
            if (res.success) setAllProducts(res.data?.data || res.data || []);
        });
        posApi.getLoyaltyConfig().then((res) => {
            if (res.success) setLoyaltyConfig(res.data);
        });
        storesApi.list({ limit: 200 }).then((res) => {
            if (!res.success) return;
            const list = res?.data?.data || [];
            const selectedStore = list.find((item) => String(item.id) === String(user?.storeId)) || list[0];
            if (selectedStore) setTaxRate(Number(selectedStore.tax_rate || 0));
        });
    }, [user?.storeId]);

    // ── Product search & scan ──────────────────────────────────────────────────
    const filtered = useMemo(() => {
        const q = query.toLowerCase().trim();
        if (!q) return allProducts.slice(0, 40);
        return allProducts.filter(
            (p) =>
                p.name?.toLowerCase().includes(q) ||
                p.sku?.toLowerCase().includes(q) ||
                p.barcode?.includes(q)
        ).slice(0, 40);
    }, [allProducts, query]);

    // ── Cart operations ────────────────────────────────────────────────────────
    const addToCart = useCallback((p) => {
        setCart((prev) => {
            const idx = prev.findIndex((i) => i.product_id === p.id);
            if (idx >= 0) {
                const copy = [...prev];
                copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + 1 };
                return copy;
            }
            return [...prev, {
                product_id: p.id,
                name: p.name,
                sku: p.sku,
                barcode: p.barcode,
                price: Number(p.selling_price),
                quantity: 1,
            }];
        });
    }, []);

    const handleScannedCode = useCallback((rawCode) => {
        const code = String(rawCode || '').trim();
        if (!code) return false;

        const exact = allProducts.find((p) => {
            const barcode = String(p.barcode || '').trim();
            const sku = String(p.sku || '').trim();
            return barcode === code || sku === code || String(p.id) === code;
        });

        if (!exact) {
            setQuery(code);
            setScanMessage(`No exact product found for ${code}.`);
            scanInputRef.current?.focus();
            return false;
        }

        if (Number(exact.quantity_in_stock || 0) <= 0) {
            setScanMessage(`${exact.name} is out of stock.`);
            return false;
        }

        addToCart(exact);
        setQuery('');
        setScanMessage(`${exact.name} added to cart.`);
        return true;
    }, [addToCart, allProducts]);

    const handleScanKey = (e) => {
        if (e.key === 'Enter' && query.trim()) {
            if (handleScannedCode(query)) {
                e.preventDefault();
            }
        }
    };

    useEffect(() => {
        const onKeyDown = (event) => {
            const target = event.target;
            const tagName = String(target?.tagName || '').toLowerCase();
            const isTypingField = ['input', 'textarea', 'select'].includes(tagName) || target?.isContentEditable;

            if (isTypingField || event.ctrlKey || event.metaKey || event.altKey) return;

            if (event.key === 'Enter') {
                if (hardwareBufferRef.current) {
                    handleScannedCode(hardwareBufferRef.current);
                    hardwareBufferRef.current = '';
                    event.preventDefault();
                }
                return;
            }

            if (event.key.length !== 1) return;
            hardwareBufferRef.current += event.key;
            clearTimeout(hardwareTimerRef.current);
            hardwareTimerRef.current = setTimeout(() => {
                hardwareBufferRef.current = '';
            }, 120);
        };

        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            clearTimeout(hardwareTimerRef.current);
        };
    }, [handleScannedCode]);

    const updateQty = (id, qty) => {
        if (qty < 0) return;
        setCart((prev) => prev.map((i) => i.product_id === id ? { ...i, quantity: qty } : i).filter((i) => i.quantity > 0));
    };

    const removeItem = (id) => setCart((prev) => prev.filter((i) => i.product_id !== id));

    const clearCart = () => {
        setCart([]);
        setCustomer(null);
        setCustomerQuery('');
        setRedeemPoints(false);
        setDiscountInput('');
        setCashTendered('');
        setPayReference('');
        setSplitCash('');
        setSplitTransfer('');
        setSplitReference('');
        setPayMethod('cash');
    };

    // ── Totals ────────────────────────────────────────────────────────────────
    const subtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.quantity, 0), [cart]);
    const discountValue = useMemo(() => {
        const v = parseFloat(discountInput) || 0;
        return Math.min(v, subtotal);
    }, [discountInput, subtotal]);

    const pointsRedeemable = useMemo(() => {
        if (!redeemPoints || !customer || !loyaltyConfig?.enabled) return 0;
        const maxVal = subtotal * Number(loyaltyConfig.max_redemption_percent);
        const maxPts = Math.floor(maxVal * Number(loyaltyConfig.points_per_redemption_unit));
        return Math.min(customer.loyalty_points || 0, maxPts);
    }, [redeemPoints, customer, loyaltyConfig, subtotal]);

    const redemptionValue = useMemo(() => {
        if (!pointsRedeemable || !loyaltyConfig) return 0;
        return pointsRedeemable / Number(loyaltyConfig.points_per_redemption_unit);
    }, [pointsRedeemable, loyaltyConfig]);

    const totalAfterDiscounts = useMemo(() => Math.max(0, subtotal - discountValue - redemptionValue), [subtotal, discountValue, redemptionValue]);
    const taxAmount = useMemo(() => {
        if (totalAfterDiscounts <= 0) return 0;
        return (totalAfterDiscounts * Number(taxRate || 0)) / 100;
    }, [totalAfterDiscounts, taxRate]);
    const totalPayable = useMemo(() => totalAfterDiscounts + taxAmount, [totalAfterDiscounts, taxAmount]);
    const changeAmount = useMemo(() => {
        if (payMethod !== 'cash') return 0;
        const tendered = parseFloat(cashTendered) || 0;
        return Math.max(0, tendered - totalPayable);
    }, [payMethod, cashTendered, totalPayable]);

    // ── Customer search ────────────────────────────────────────────────────────
    const searchCustomers = useCallback(async (q) => {
        if (!q.trim()) { setCustomerSuggestions([]); return; }
        const res = await posApi.searchCustomers(q);
        if (res.success) setCustomerSuggestions(res.data || []);
    }, []);

    useEffect(() => {
        const t = setTimeout(() => searchCustomers(customerQuery), 300);
        return () => clearTimeout(t);
    }, [customerQuery, searchCustomers]);

    const getCustomerName = (c) => {
        const fullName = `${c?.first_name || ''} ${c?.last_name || ''}`.trim();
        return fullName || 'Walk-in customer';
    };

    const getCustomerInitials = (c) => {
        const first = (c?.first_name || '').trim();
        const last = (c?.last_name || '').trim();
        if (first || last) return `${first[0] || ''}${last[0] || ''}`.toUpperCase();
        return 'WC';
    };

    const selectCustomer = (c) => {
        setCustomer(c);
        setCustomerQuery(`${getCustomerName(c)} — ${c.phone}`);
        setCustomerSuggestions([]);
    };

    const saveNewCustomer = async () => {
        if (!newCust.phone) return;
        setCustLoading(true);
        const res = await posApi.createCustomer(newCust);
        setCustLoading(false);
        if (res.success) {
            selectCustomer(res.data);
            setShowNewCustomer(false);
            setNewCust({ first_name: '', last_name: '', phone: '', email: '' });
        } else {
            alert(res.error || 'Failed to save customer');
        }
    };

    // ── Checkout ──────────────────────────────────────────────────────────────
    const canCharge = useMemo(() => {
        if (!cart.length || processing) return false;
        if (payMethod === 'cash') return parseFloat(cashTendered) >= totalPayable;
        if (payMethod === 'transfer' || payMethod === 'pos') return payReference.trim().length > 0;
        if (payMethod === 'split') {
            const sc = parseFloat(splitCash) || 0;
            const st = parseFloat(splitTransfer) || 0;
            return sc + st >= totalPayable && splitReference.trim().length > 0;
        }
        return false;
    }, [cart, processing, payMethod, cashTendered, payReference, totalPayable, splitCash, splitTransfer, splitReference]);

    const handleCheckout = async () => {
        if (!canCharge) return;
        setProcessing(true);

        const payload = {
            items: cart.map(({ product_id, quantity, price }) => ({ product_id, quantity, price })),
            customer_id: customer?.id || null,
            payment_method: payMethod,
            payment_reference: payMethod === 'transfer' || payMethod === 'pos' ? payReference : undefined,
            cash_tendered: payMethod === 'cash' ? parseFloat(cashTendered) : undefined,
            split_cash: payMethod === 'split' ? parseFloat(splitCash) : undefined,
            split_transfer: payMethod === 'split' ? parseFloat(splitTransfer) : undefined,
            split_reference: payMethod === 'split' ? splitReference : undefined,
            discount_amount: discountValue,
            tax_amount: taxAmount,
            loyalty_points_redeemed: redeemPoints ? pointsRedeemable : 0,
        };

        const res = await posApi.createTransaction(payload);
        setProcessing(false);

        if (res.success) {
            // Build receipt data for modal
            setReceipt({
                transaction: res.data,
                cart: [...cart],
                customer,
                subtotal,
                discountValue,
                redemptionValue,
                taxAmount,
                taxRate,
                totalAfterDiscounts: totalPayable,
                changeAmount: res.data.change_amount ?? changeAmount,
                pointsRedeemed: res.data.loyalty_points_redeemed ?? 0,
                payMethod,
                payReference: payReference || splitReference || '',
                cashierName: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
                storeName: user?.tenant_name || user?.tenantName || 'POS Store',
            });
            clearCart();
        } else {
            alert(typeof res.error === 'string' ? res.error : 'Checkout failed');
        }
    };

    const loyaltyEnabled = loyaltyConfig?.enabled;
    const canRedeem = loyaltyEnabled && customer && (customer.loyalty_points || 0) >= (loyaltyConfig?.min_redemption_points || 500);

    return (
        <div className="cashier-shell">
            {/* ── LEFT: Product Panel ── */}
            <section className="cashier-products panel">
                <div className="cashier-panel-header">
                    <h3>Products</h3>
                    <span className="muted-sm">{filtered.length} shown</span>
                </div>
                <div className="cashier-scan-bar">
                    <span className="scan-icon">&#x2317;</span>
                    <input
                        ref={scanInputRef}
                        className="cashier-scan-input"
                        placeholder="Scan barcode, use camera, or search by name / SKU..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleScanKey}
                        autoFocus
                    />
                    <button className="scan-camera-btn" type="button" onClick={() => setScannerOpen(true)}>Camera</button>
                    {query && <button className="scan-clear-btn" onClick={() => setQuery('')}>✕</button>}
                </div>
                {scanMessage ? <p className="scanner-inline-status">{scanMessage}</p> : null}

                <div className="cashier-products-grid">
                    {filtered.map((p) => (
                        <button
                            key={p.id}
                            className={`cashier-product-card${p.quantity_in_stock <= 0 ? ' out-of-stock' : ''}`}
                            onClick={() => addToCart(p)}
                            disabled={p.quantity_in_stock <= 0}
                        >
                            <div className="cpcard-name">{p.name}</div>
                            <div className="cpcard-price">₦{fmt(p.selling_price)}</div>
                            <div className={`cpcard-stock${p.quantity_in_stock <= 5 ? ' low' : ''}`}>
                                {p.quantity_in_stock <= 0 ? 'Out' : `${p.quantity_in_stock} in stock`}
                            </div>
                        </button>
                    ))}
                    {filtered.length === 0 && <p className="empty-hint">No products match "{query}"</p>}
                </div>
            </section>

            {/* ── MIDDLE: Cart Panel ── */}
            <section className="cashier-cart panel">
                <div className="cashier-panel-header">
                    <h3>Cart</h3>
                    {cart.length > 0 && <button className="btn-link-danger" onClick={clearCart}>Clear</button>}
                </div>

                {/* Customer search */}
                <div className="cart-customer-block">
                    <label className="field-label">Customer (optional)</label>
                    <div className="customer-search-wrap">
                        <input
                            placeholder="Search by name or phone…"
                            value={customerQuery}
                            onChange={(e) => { setCustomerQuery(e.target.value); setCustomer(null); }}
                        />
                        {customer && (
                            <button className="cust-clear-btn" onClick={() => { setCustomer(null); setCustomerQuery(''); setRedeemPoints(false); }}>✕</button>
                        )}
                    </div>
                    {customerSuggestions.length > 0 && !customer && (
                        <ul className="customer-dropdown">
                            {customerSuggestions.map((c) => (
                                <li key={c.id} onClick={() => selectCustomer(c)}>
                                    <strong>{getCustomerName(c)}</strong>
                                    <span>{c.phone}</span>
                                    {loyaltyEnabled && <span className="pts-badge">{c.loyalty_points || 0} pts</span>}
                                </li>
                            ))}
                        </ul>
                    )}
                    {customer && (
                        <div className="customer-chip">
                            <div className="cust-initials">{getCustomerInitials(customer)}</div>
                            <div>
                                <strong>{getCustomerName(customer)}</strong>
                                <span className="muted-sm">{customer.phone}</span>
                            </div>
                            {loyaltyEnabled && <span className="pts-badge">{customer.loyalty_points || 0} pts</span>}
                        </div>
                    )}
                    {!customer && (
                        <button className="btn-link" onClick={() => setShowNewCustomer((v) => !v)}>
                            {showNewCustomer ? '− Cancel' : '+ New customer'}
                        </button>
                    )}
                    {showNewCustomer && (
                        <div className="new-customer-form">
                            <input placeholder="First name (optional)" value={newCust.first_name} onChange={(e) => setNewCust((p) => ({ ...p, first_name: e.target.value }))} />
                            <input placeholder="Last name (optional)" value={newCust.last_name} onChange={(e) => setNewCust((p) => ({ ...p, last_name: e.target.value }))} />
                            <input placeholder="Phone *" value={newCust.phone} onChange={(e) => setNewCust((p) => ({ ...p, phone: e.target.value }))} />
                            <input placeholder="Email" value={newCust.email} onChange={(e) => setNewCust((p) => ({ ...p, email: e.target.value }))} />
                            <button className="btn btn-sm" onClick={saveNewCustomer} disabled={custLoading}>
                                {custLoading ? 'Saving…' : 'Save & attach'}
                            </button>
                        </div>
                    )}
                </div>

                {/* Cart items */}
                <div className="cart-items">
                    {cart.length === 0 && <p className="empty-hint">Add products to begin a sale</p>}
                    {cart.map((item) => (
                        <div className="cart-row" key={item.product_id}>
                            <div className="cart-row-info">
                                <span className="cart-item-name">{item.name}</span>
                                <span className="cart-item-unit muted-sm">₦{fmt(item.price)} ea.</span>
                            </div>
                            <div className="qty-ctrl">
                                <button onClick={() => updateQty(item.product_id, item.quantity - 1)}>−</button>
                                <input
                                    type="number"
                                    min="1"
                                    value={item.quantity}
                                    onChange={(e) => updateQty(item.product_id, parseInt(e.target.value) || 0)}
                                />
                                <button onClick={() => updateQty(item.product_id, item.quantity + 1)}>+</button>
                            </div>
                            <div className="cart-row-total">₦{fmt(item.price * item.quantity)}</div>
                            <button className="cart-remove-btn" onClick={() => removeItem(item.product_id)}>✕</button>
                        </div>
                    ))}
                </div>

                {/* Discount */}
                <div className="cart-discount-row">
                    <label className="field-label">Discount (₦)</label>
                    <input
                        type="number"
                        min="0"
                        placeholder="0.00"
                        value={discountInput}
                        onChange={(e) => setDiscountInput(e.target.value)}
                    />
                </div>

                {/* Loyalty redemption */}
                {canRedeem && (
                    <div className="loyalty-redeem-row">
                        <label className="toggle-label">
                            <input type="checkbox" checked={redeemPoints} onChange={(e) => setRedeemPoints(e.target.checked)} />
                            Redeem {pointsRedeemable} pts (≈ ₦{fmt(redemptionValue)})
                        </label>
                    </div>
                )}

                {/* Summary */}
                <div className="cart-summary">
                    <div className="summary-row"><span>Subtotal</span><span>₦{fmt(subtotal)}</span></div>
                    {discountValue > 0 && <div className="summary-row discount-row"><span>Discount</span><span>−₦{fmt(discountValue)}</span></div>}
                    {redemptionValue > 0 && <div className="summary-row discount-row"><span>Loyalty redemption</span><span>−₦{fmt(redemptionValue)}</span></div>}
                    <div className="summary-row"><span>Tax ({Number(taxRate || 0).toFixed(2)}%)</span><span>₦{fmt(taxAmount)}</span></div>
                    <div className="summary-row total-row"><span>Total</span><span>₦{fmt(totalPayable)}</span></div>
                </div>
            </section>

            {/* ── RIGHT: Payment Panel ── */}
            <section className="cashier-payment panel">
                <div className="cashier-panel-header">
                    <h3>Payment</h3>
                </div>

                {/* Method tabs */}
                <div className="payment-tabs">
                    {['cash', 'transfer', 'pos', 'split'].map((m) => (
                        <button key={m} className={`pay-tab${payMethod === m ? ' active' : ''}`} onClick={() => setPayMethod(m)}>
                            {m === 'pos' ? 'POS/Card' : m.charAt(0).toUpperCase() + m.slice(1)}
                        </button>
                    ))}
                </div>

                {/* Cash */}
                {payMethod === 'cash' && (
                    <div className="payment-fields">
                        <label className="field-label">Amount Tendered (₦)</label>
                        <input
                            type="number"
                            placeholder="0.00"
                            value={cashTendered}
                            onChange={(e) => setCashTendered(e.target.value)}
                            min={totalPayable}
                        />
                        {parseFloat(cashTendered) > 0 && (
                            <div className={`change-display${changeAmount > 0 ? ' positive' : ''}`}>
                                <span>Change</span>
                                <strong>₦{fmt(changeAmount)}</strong>
                            </div>
                        )}
                        {/* Quick tender buttons */}
                        <div className="quick-tender-strip">
                            {[500, 1000, 2000, 5000].map((v) => (
                                <button key={v} className="quick-tender-btn" onClick={() => setCashTendered(String(v))}>₦{v.toLocaleString()}</button>
                            ))}
                            <button className="quick-tender-btn exact-btn" onClick={() => setCashTendered(String(Math.ceil(totalPayable)))}>Exact</button>
                        </div>
                    </div>
                )}

                {/* Transfer / POS */}
                {(payMethod === 'transfer' || payMethod === 'pos') && (
                    <div className="payment-fields">
                        <label className="field-label">Payment Reference ID *</label>
                        <input
                            placeholder="Enter reference / transaction ID"
                            value={payReference}
                            onChange={(e) => setPayReference(e.target.value)}
                        />
                        <p className="pay-hint">{payMethod === 'transfer' ? 'Bank transfer' : 'POS/Card'} — enter the reference number printed on the slip or sent via SMS.</p>
                    </div>
                )}

                {/* Split */}
                {payMethod === 'split' && (
                    <div className="payment-fields">
                        <label className="field-label">Cash amount (₦)</label>
                        <input type="number" placeholder="0.00" value={splitCash} onChange={(e) => setSplitCash(e.target.value)} />
                        <label className="field-label">Transfer / POS amount (₦)</label>
                        <input type="number" placeholder="0.00" value={splitTransfer} onChange={(e) => setSplitTransfer(e.target.value)} />
                        <label className="field-label">Transfer reference *</label>
                        <input placeholder="Reference number" value={splitReference} onChange={(e) => setSplitReference(e.target.value)} />
                        {(parseFloat(splitCash) || 0) + (parseFloat(splitTransfer) || 0) >= totalPayable && (
                            <div className="change-display positive">
                                <span>Change (cash)</span>
                                <strong>₦{fmt(Math.max(0, (parseFloat(splitCash) || 0) - (totalPayable - (parseFloat(splitTransfer) || 0))))}</strong>
                            </div>
                        )}
                    </div>
                )}

                <div className="payment-total-display">
                    <span>Amount due</span>
                    <strong>₦{fmt(totalPayable)}</strong>
                </div>

                <button
                    className="btn charge-btn"
                    onClick={handleCheckout}
                    disabled={!canCharge}
                >
                    {processing ? 'Processing…' : `Charge ₦${fmt(totalPayable)}`}
                </button>

                {loyaltyEnabled && customer && (
                    <div className="cashier-loyalty-info">
                        <span>&#9733; {getCustomerName(customer)} points: <strong>{customer.loyalty_points || 0}</strong></span>
                    </div>
                )}
            </section>

            {receipt && (
                <ReceiptModal
                    data={receipt}
                    onClose={() => setReceipt(null)}
                />
            )}
            {scannerOpen ? (
                <BarcodeScannerModal
                    onClose={() => setScannerOpen(false)}
                    onScan={(code) => {
                        handleScannedCode(code);
                        setScannerOpen(false);
                    }}
                />
            ) : null}
        </div>
    );
}
